"use client"

import * as React from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Bell,
  CheckCircle2,
  AlertCircle,
  Info,
  AlertTriangle,
  Trash2,
  CheckCheck,
  BellOff,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useNotificationsStore, type AppNotification } from "@/store/use-notifications-store"

/** Type-specific icons with brand colors */
function NotificationIcon({ type }: { type: AppNotification["type"] }) {
  switch (type) {
    case "success":
      return <CheckCircle2 className="size-5 shrink-0 text-green-500" />
    case "error":
      return <AlertCircle className="size-5 shrink-0 text-red-500" />
    case "warning":
      return <AlertTriangle className="size-5 shrink-0 text-amber-500" />
    case "info":
      return <Info className="size-5 shrink-0 text-brand" />
  }
}

/** Simple relative timestamp in Russian */
function relativeTime(timestamp: number): string {
  const now = Date.now()
  const diff = Math.floor((now - timestamp) / 1000)

  if (diff < 60) return "только что"
  if (diff < 3600) {
    const mins = Math.floor(diff / 60)
    return `${mins} мин. назад`
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600)
    return `${hours} ч. назад`
  }
  const days = Math.floor(diff / 86400)
  if (days === 1) return "вчера"
  if (days < 5) return `${days} дня назад`
  return `${days} дней назад`
}

function NotificationItem({
  notification,
  onMarkRead,
  onRemove,
}: {
  notification: AppNotification
  onMarkRead: (id: string) => void
  onRemove: (id: string) => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 8 }}
      className={`group flex gap-3 rounded-lg p-3 transition-colors cursor-pointer ${
        notification.read
          ? "hover:bg-muted/50"
          : "bg-brand/5 hover:bg-brand/10"
      }`}
      onClick={() => {
        if (!notification.read) onMarkRead(notification.id)
      }}
    >
      <NotificationIcon type={notification.type} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-snug">
            {notification.title}
          </p>
          {!notification.read && (
            <span className="mt-1 shrink-0 size-2 rounded-full bg-brand" />
          )}
        </div>
        {notification.message && (
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
            {notification.message}
          </p>
        )}
        <span className="mt-1 block text-[11px] text-muted-foreground/70">
          {relativeTime(notification.timestamp)}
        </span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onRemove(notification.id)
        }}
        className="shrink-0 self-start rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
        aria-label="Удалить"
      >
        <Trash2 className="size-3.5" />
      </button>
    </motion.div>
  )
}

/** Desktop popover content */
function NotificationPopoverContent() {
  const items = useNotificationsStore((s) => s.items)
  const markAsRead = useNotificationsStore((s) => s.markAsRead)
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead)
  const clearAll = useNotificationsStore((s) => s.clearAll)
  const removeNotification = useNotificationsStore((s) => s.removeNotification)

  return (
    <PopoverContent align="end" className="w-80 p-0">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <span className="text-sm font-semibold">Уведомления</span>
        <div className="flex items-center gap-1">
          {items.length > 0 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={markAllAsRead}
              >
                <CheckCheck className="mr-1 size-3.5" />
                Прочитать все
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                onClick={clearAll}
              >
                <Trash2 className="mr-1 size-3.5" />
                Очистить
              </Button>
            </>
          )}
        </div>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <BellOff className="mb-3 size-8 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">Нет уведомлений</p>
        </div>
      ) : (
        <ScrollArea className="max-h-[360px]">
          <AnimatePresence initial={false}>
            <div className="p-2">
              {items.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onMarkRead={markAsRead}
                  onRemove={removeNotification}
                />
              ))}
            </div>
          </AnimatePresence>
        </ScrollArea>
      )}
    </PopoverContent>
  )
}

/** Mobile sheet content */
function NotificationSheetContent() {
  const items = useNotificationsStore((s) => s.items)
  const markAsRead = useNotificationsStore((s) => s.markAsRead)
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead)
  const clearAll = useNotificationsStore((s) => s.clearAll)
  const removeNotification = useNotificationsStore((s) => s.removeNotification)

  return (
    <SheetContent side="bottom" className="max-h-[80vh] rounded-t-2xl">
      <SheetHeader className="border-b pb-3">
        <div className="flex items-center justify-between">
          <SheetTitle className="text-left">Уведомления</SheetTitle>
          {items.length > 0 && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-xs"
                onClick={markAllAsRead}
              >
                <CheckCheck className="mr-1 size-3.5" />
                Прочитать все
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-xs text-muted-foreground hover:text-destructive"
                onClick={clearAll}
              >
                <Trash2 className="mr-1 size-3.5" />
                Очистить
              </Button>
            </div>
          )}
        </div>
      </SheetHeader>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <BellOff className="mb-3 size-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">Нет уведомлений</p>
        </div>
      ) : (
        <ScrollArea className="flex-1">
          <AnimatePresence initial={false}>
            <div className="p-2">
              {items.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onMarkRead={markAsRead}
                  onRemove={removeNotification}
                />
              ))}
            </div>
          </AnimatePresence>
        </ScrollArea>
      )}
    </SheetContent>
  )
}

export function NotificationCenter() {
  const unreadCount = useNotificationsStore((s) => s.unreadCount)
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [hydrated, setHydrated] = React.useState(false)
  React.useEffect(() => { setHydrated(true) }, [])
  const count = unreadCount()

  return (
    <>
      {/* Desktop: Popover */}
      <div className="hidden md:block">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              aria-label="Уведомления"
            >
              <Bell className="size-5" />
              {hydrated && count > 0 && (
                <Badge className="absolute -top-1 -right-1 size-5 justify-center rounded-full p-0 text-[10px] bg-red-500 counter-animate">
                  {count > 9 ? "9+" : count}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <NotificationPopoverContent />
        </Popover>
      </div>

      {/* Mobile: Sheet (bottom slide-up) */}
      <div className="md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              aria-label="Уведомления"
            >
              <Bell className="size-5" />
              {hydrated && count > 0 && (
                <Badge className="absolute -top-1 -right-1 size-5 justify-center rounded-full p-0 text-[10px] bg-red-500 counter-animate">
                  {count > 9 ? "9+" : count}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <NotificationSheetContent />
        </Sheet>
      </div>
    </>
  )
}
